'use client';

import { useMemo, useState } from 'react';
import { Box, Button, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';

type DonutDatum = {
  label: string;
  value: number;
};

export type CashHistoryPoint = {
  date: string;
  cash: number | null;
  liquidAssets: number | null;
  receivable: number | null;
  payable: number | null;
  payableRendimentosDistribuir: number | null;
  payableTaxaAdministracao: number | null;
  payableTaxaPerformance: number | null;
  payableOutros: number | null;
  payableTotalPassivo: number | null;
  payableUsedTotalPassivoFallback: boolean;
};

export type RealEstateRiskHistoryPoint = {
  date: string;
  revenuePercentTotal: number | null;
  vacancyPercent: number | null;
  delinquencyPercent: number | null;
  occupancyPercent: number | null;
};

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 2,
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});
const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

const formatOptionalCurrency = (value: number | null) => (value === null ? '-' : CURRENCY_FORMATTER.format(value));

const formatCompactCurrency = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000_000)} bi`;
  }
  if (abs >= 1_000_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000)} mi`;
  }
  if (abs >= 1_000) {
    return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000)} mil`;
  }
  return CURRENCY_FORMATTER.format(value);
};

const formatOptionalPercent = (value: number | null) => (value === null ? '-' : `${PERCENT_FORMATTER.format(value)}%`);

const donutBackground = (items: DonutDatum[], colors: string[]) => {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  if (total <= 0 || items.length === 0) return 'hsl(var(--muted))';
  let progress = 0;
  const slices = items.map((item, index) => {
    const start = progress * 100;
    progress += item.value / total;
    const end = progress * 100;
    return `${colors[index % colors.length]} ${start}% ${end}%`;
  });
  return `conic-gradient(${slices.join(', ')})`;
};

export const SectionTable = ({ headers, rows }: { headers: string[]; rows: Array<Array<string>> }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Box
      component='table'
      sx={{
        width: '100%',
        minWidth: 700,
        borderCollapse: 'collapse',
        '& th, & td': {
          borderBottom: '1px solid hsl(var(--border))',
          px: 1,
          py: 0.9,
          textAlign: 'left',
          fontSize: 13,
          whiteSpace: 'nowrap',
        },
        '& th': {
          color: 'text.secondary',
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        },
      }}
    >
      <Box component='thead'>
        <Box component='tr'>
          {headers.map((header) => (
            <Box key={header} component='th'>
              {header}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component='tbody'>
        {rows.map((row, idx) => (
          <Box component='tr' key={idx}>
            {row.map((value, col) => (
              <Box component='td' key={`${idx}-${col}`}>
                {value}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

export function InfoDotTooltip({ text }: { text: string }) {
  return (
    <Tooltip title={text} arrow placement='top'>
      <Box
        component='span'
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: '1px solid hsl(var(--border))',
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          cursor: 'help',
          userSelect: 'none',
        }}
        aria-label='Informações da seção'
      >
        i
      </Box>
    </Tooltip>
  );
}

export function DonutBlock({
  title,
  items,
  colors,
  valueKind = 'count',
  infoText,
}: {
  title: string;
  items: DonutDatum[];
  colors: string[];
  valueKind?: 'count' | 'currency' | 'percentPoints';
  infoText?: string;
}) {
  const total = items.reduce((acc, item) => acc + item.value, 0);
  const background = donutBackground(items, colors);
  if (items.length === 0 || total <= 0) return null;
  const formatValue = (value: number) => {
    if (valueKind === 'currency') return formatCompactCurrency(value);
    if (valueKind === 'percentPoints') return `${PERCENT_FORMATTER.format(value)} pp`;
    return NUMBER_FORMATTER.format(value);
  };
  return (
    <Paper variant='outlined' sx={{ p: 1.25 }}>
      <Stack direction='row' spacing={0.6} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
        {infoText ? <InfoDotTooltip text={infoText} /> : null}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '160px minmax(0, 1fr)' },
          gap: 1.2,
          alignItems: 'start',
        }}
      >
        <Stack spacing={1} alignItems='center'>
          <Box
            sx={{
              width: { xs: 128, sm: 140 },
              height: { xs: 128, sm: 140 },
              borderRadius: '50%',
              background,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                width: { xs: 76, sm: 84 },
                height: { xs: 76, sm: 84 },
                borderRadius: '50%',
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Total
              </Typography>
              <Typography variant='subtitle2' fontWeight={800}>
                {valueKind === 'currency' ? (
                  <Tooltip title={formatOptionalCurrency(total)} arrow>
                    <Box component='span' sx={{ cursor: 'help' }}>
                      {formatValue(total)}
                    </Box>
                  </Tooltip>
                ) : (
                  formatValue(total)
                )}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
          {items.map((item, index) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            return (
              <Stack key={`${title}-${item.label}-${index}`} spacing={0.35}>
                <Stack direction='row' justifyContent='space-between' spacing={1} sx={{ minWidth: 0, width: '100%' }}>
                  <Stack direction='row' spacing={0.7} alignItems='center' sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: colors[index % colors.length],
                      }}
                    />
                    <Tooltip title={item.label} arrow>
                      <Typography
                        variant='body2'
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <Typography
                    variant='body2'
                    fontWeight={700}
                    sx={{
                      flexShrink: 0,
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      pl: 0.5,
                    }}
                  >
                    {valueKind === 'currency' ? (
                      <Tooltip title={formatOptionalCurrency(item.value)} arrow>
                        <Box component='span' sx={{ cursor: 'help' }}>
                          {formatValue(item.value)}
                        </Box>
                      </Tooltip>
                    ) : (
                      formatValue(item.value)
                    )}{' '}
                    • {PERCENT_FORMATTER.format(pct)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant='determinate'
                  value={pct}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: 'hsl(var(--muted))',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors[index % colors.length],
                    },
                  }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Paper>
  );
}

export function CashHistoryChart({ points }: { points: CashHistoryPoint[] }) {
  const colors = useAppColors();
  const cashColor = colors.charts.portfolioMix.realEstate;
  const liquidAssetsColor = colors.semantic.warning;
  const receivableColor = colors.charts.portfolioMix.funds;
  const payableColor = colors.semantic.danger;
  const valid = points.filter(
    (item) => item.cash !== null || item.liquidAssets !== null || item.receivable !== null || item.payable !== null,
  );
  if (valid.length < 2) return null;
  const ordered = [...valid].sort((a, b) => a.date.localeCompare(b.date));
  const [showCash, setShowCash] = useState(true);
  const [showLiquidAssets, setShowLiquidAssets] = useState(true);
  const [showReceivable, setShowReceivable] = useState(true);
  const [showPayable, setShowPayable] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const width = 980;
  const height = 300;
  const left = 78;
  const right = width - 28;
  const top = 22;
  const bottom = height - 48;
  const chartWidth = right - left;
  const chartHeight = bottom - top;
  const activeMax = Math.max(
    1,
    ...(showCash ? ordered.map((item) => item.cash ?? 0) : [0]),
    ...(showLiquidAssets ? ordered.map((item) => item.liquidAssets ?? 0) : [0]),
    ...(showReceivable ? ordered.map((item) => item.receivable ?? 0) : [0]),
    ...(showPayable ? ordered.map((item) => item.payable ?? 0) : [0]),
  );
  const minTs = new Date(`${ordered[0].date}T00:00:00Z`).getTime();
  const maxTs = new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`).getTime();
  const mapX = (date: string) => {
    const ts = new Date(`${date}T00:00:00Z`).getTime();
    if (maxTs === minTs) return left + chartWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * chartWidth;
  };
  const mapY = (value: number) => {
    const v = Math.max(0, value);
    return bottom - (v / activeMax) * chartHeight;
  };
  const poly = (selector: (item: (typeof ordered)[number]) => number | null) =>
    ordered
      .map((item) => {
        const v = selector(item);
        if (v === null) return null;
        return `${mapX(item.date)},${mapY(v)}`;
      })
      .filter((point): point is string => point !== null)
      .join(' ');

  return (
    <Box>
      <Stack direction='row' spacing={0.8} useFlexGap flexWrap='wrap' sx={{ mb: 0.8 }}>
        <Button
          size='small'
          variant={showCash ? 'contained' : 'outlined'}
          onClick={() => setShowCash((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showCash ? cashColor : undefined,
            borderColor: cashColor,
            color: showCash ? colors.semantic.white : cashColor,
          }}
        >
          Caixa
        </Button>
        <Button
          size='small'
          variant={showLiquidAssets ? 'contained' : 'outlined'}
          onClick={() => setShowLiquidAssets((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showLiquidAssets ? liquidAssetsColor : undefined,
            borderColor: liquidAssetsColor,
            color: showLiquidAssets ? colors.semantic.white : liquidAssetsColor,
          }}
        >
          RF + Títulos
        </Button>
        <Button
          size='small'
          variant={showReceivable ? 'contained' : 'outlined'}
          onClick={() => setShowReceivable((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showReceivable ? receivableColor : undefined,
            borderColor: receivableColor,
            color: showReceivable ? colors.semantic.white : receivableColor,
          }}
        >
          A receber
        </Button>
        <Button
          size='small'
          variant={showPayable ? 'contained' : 'outlined'}
          onClick={() => setShowPayable((v) => !v)}
          sx={{
            textTransform: 'none',
            bgcolor: showPayable ? payableColor : undefined,
            borderColor: payableColor,
            color: showPayable ? colors.semantic.white : payableColor,
          }}
        >
          A pagar
        </Button>
      </Stack>
      <Box sx={{ width: '100%' }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width='100%'
          role='img'
          onMouseLeave={() => setHoveredIndex(null)}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            if (rect.width <= 0 || chartWidth <= 0) return;
            const relativeX = ((event.clientX - rect.left) / rect.width) * width;
            const clampedX = Math.max(left, Math.min(right, relativeX));
            const ratio = (clampedX - left) / chartWidth;
            const index = Math.round(ratio * (ordered.length - 1));
            setHoveredIndex(Math.max(0, Math.min(ordered.length - 1, index)));
          }}
        >
          <rect
            x={left}
            y={top}
            width={chartWidth}
            height={chartHeight}
            fill='transparent'
            stroke='hsl(var(--border))'
          />
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
            const y = bottom - tick * chartHeight;
            const value = activeMax * tick;
            return (
              <g key={tick}>
                <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
                <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                  {formatCompactCurrency(value)}
                </text>
              </g>
            );
          })}
          {showCash ? (
            <polyline points={poly((item) => item.cash)} fill='none' stroke={cashColor} strokeWidth='2' />
          ) : null}
          {showLiquidAssets ? (
            <polyline
              points={poly((item) => item.liquidAssets)}
              fill='none'
              stroke={liquidAssetsColor}
              strokeWidth='2'
            />
          ) : null}
          {showReceivable ? (
            <polyline points={poly((item) => item.receivable)} fill='none' stroke={receivableColor} strokeWidth='2' />
          ) : null}
          {showPayable ? (
            <polyline
              points={poly((item) => item.payable)}
              fill='none'
              stroke={payableColor}
              strokeWidth='2'
              strokeDasharray='5 3'
            />
          ) : null}
          {hoveredIndex !== null
            ? (() => {
                const row = ordered[hoveredIndex];
                const x = mapX(row.date);
                const payableDetailLines = row.payableUsedTotalPassivoFallback
                  ? [`  - Total passivo (fallback): ${formatOptionalCurrency(row.payableTotalPassivo)}`]
                  : [
                      `  - Rend. a distribuir: ${formatOptionalCurrency(row.payableRendimentosDistribuir)}`,
                      `  - Taxa adm.: ${formatOptionalCurrency(row.payableTaxaAdministracao)}`,
                      `  - Taxa perf.: ${formatOptionalCurrency(row.payableTaxaPerformance)}`,
                      `  - Outros a pagar: ${formatOptionalCurrency(row.payableOutros)}`,
                    ];
                const tooltipHeight = 103 + payableDetailLines.length * 16;
                return (
                  <>
                    <line
                      x1={x}
                      y1={top}
                      x2={x}
                      y2={bottom}
                      stroke='hsl(var(--muted-foreground) / 0.6)'
                      strokeDasharray='4 4'
                    />
                    <g transform={`translate(${Math.min(right - 212, Math.max(left + 8, x + 8))}, ${top + 8})`}>
                      <rect
                        width='212'
                        height={tooltipHeight}
                        rx='8'
                        fill='hsl(var(--card))'
                        stroke='hsl(var(--border))'
                      />
                      <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                        {DATE_FORMATTER.format(new Date(`${row.date}T00:00:00Z`))}
                      </text>
                      <text
                        x='10'
                        y='36'
                        fontSize='12'
                        fill={cashColor}
                      >{`Caixa: ${formatOptionalCurrency(row.cash)}`}</text>
                      <text
                        x='10'
                        y='53'
                        fontSize='12'
                        fill={receivableColor}
                      >{`A receber: ${formatOptionalCurrency(row.receivable)}`}</text>
                      <text
                        x='10'
                        y='70'
                        fontSize='12'
                        fill={liquidAssetsColor}
                      >{`RF + títulos: ${formatOptionalCurrency(row.liquidAssets)}`}</text>
                      <text
                        x='10'
                        y='87'
                        fontSize='12'
                        fill={payableColor}
                      >{`A pagar: ${formatOptionalCurrency(row.payable)}`}</text>
                      {payableDetailLines.map((line, index) => (
                        <text
                          key={`${row.date}-payable-detail-${index}`}
                          x='14'
                          y={105 + index * 16}
                          fontSize='11'
                          fill='hsl(var(--muted-foreground))'
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  </>
                );
              })()
            : null}
          <text x={left} y={height - 12} fontSize='12' fill='hsl(var(--muted-foreground))'>
            {DATE_FORMATTER.format(new Date(`${ordered[0].date}T00:00:00Z`))}
          </text>
          <text x={right} y={height - 12} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
            {DATE_FORMATTER.format(new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`))}
          </text>
        </svg>
      </Box>
    </Box>
  );
}

export function RealEstateRiskHistoryChart({ points }: { points: RealEstateRiskHistoryPoint[] }) {
  const colors = useAppColors();
  const vacancyColor = colors.semantic.warningStrong;
  const delinquencyColor = colors.semantic.danger;
  const occupancyColor = colors.charts.market.dbgg;
  const [range, setRange] = useState<'6M' | '12M' | '24M' | 'TUDO'>('12M');
  const ordered = useMemo(() => [...points].sort((a, b) => a.date.localeCompare(b.date)), [points]);
  const filtered = useMemo(() => {
    if (ordered.length === 0 || range === 'TUDO') return ordered;
    const monthsBack = range === '6M' ? 6 : range === '12M' ? 12 : 24;
    const latest = new Date(`${ordered[ordered.length - 1].date}T00:00:00Z`);
    const threshold = new Date(Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - monthsBack, 1));
    const scoped = ordered.filter((item) => new Date(`${item.date}T00:00:00Z`) >= threshold);
    return scoped.length > 0 ? scoped : ordered;
  }, [ordered, range]);
  const chartData = useMemo(
    () =>
      filtered
        .map((item) => {
          const vacancyRaw = item.vacancyPercent;
          const occupancyRaw =
            item.occupancyPercent !== null ? item.occupancyPercent : vacancyRaw !== null ? 100 - vacancyRaw : null;
          const delinquencyRaw = item.delinquencyPercent;
          const vacancy = vacancyRaw === null ? null : Math.max(0, Math.min(100, vacancyRaw));
          const occupancy = occupancyRaw === null ? null : Math.max(0, Math.min(100, occupancyRaw));
          const delinquency = delinquencyRaw === null ? null : Math.max(0, Math.min(100, delinquencyRaw));
          return {
            date: item.date,
            vacancy,
            occupancy,
            delinquency,
          };
        })
        .filter((item) => item.vacancy !== null || item.occupancy !== null),
    [filtered],
  );
  if (chartData.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem leitura de vacância/ocupação no período selecionado.
      </Typography>
    );
  }

  const latestRow = chartData[chartData.length - 1];
  const vacancySeries = chartData.map((item) => item.vacancy).filter((value): value is number => value !== null);
  const averageVacancy =
    vacancySeries.length > 0 ? vacancySeries.reduce((acc, value) => acc + value, 0) / vacancySeries.length : null;
  const minVacancy = vacancySeries.length > 0 ? Math.min(...vacancySeries) : null;
  const maxVacancy = vacancySeries.length > 0 ? Math.max(...vacancySeries) : null;
  const lookbackIndex = Math.max(0, chartData.length - 4);
  const baseline = chartData[lookbackIndex]?.vacancy ?? null;
  const vacancyDelta3m = latestRow.vacancy !== null && baseline !== null ? latestRow.vacancy - baseline : null;
  const trendLabel =
    vacancyDelta3m === null
      ? 'Sem base'
      : vacancyDelta3m < 0
        ? `Queda de ${PERCENT_FORMATTER.format(Math.abs(vacancyDelta3m))} pp`
        : vacancyDelta3m > 0
          ? `Alta de ${PERCENT_FORMATTER.format(vacancyDelta3m)} pp`
          : 'Estável';
  const width = 980;
  const height = 360;
  const left = 56;
  const right = width - 20;
  const top = 52;
  const bottom = height - 62;
  const chartWidth = right - left;
  const chartHeight = bottom - top;
  const barsCount = chartData.length;
  const barGap = Math.min(14, Math.max(6, chartWidth / Math.max(20, barsCount * 2)));
  const barWidth =
    barsCount > 0 ? Math.max(16, (chartWidth - barGap * Math.max(0, barsCount - 1)) / barsCount) : chartWidth;
  const monthLabel = (date: string) =>
    new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' })
      .format(new Date(`${date}T00:00:00Z`))
      .replace('.', '');

  const summaryItems = [
    {
      label: 'Vacância atual',
      value: formatOptionalPercent(latestRow.vacancy),
    },
    {
      label: 'Média do período',
      value: formatOptionalPercent(averageVacancy),
    },
    {
      label: 'Faixa (mín-máx)',
      value:
        minVacancy !== null && maxVacancy !== null
          ? `${PERCENT_FORMATTER.format(minVacancy)}% - ${PERCENT_FORMATTER.format(maxVacancy)}%`
          : '-',
    },
    {
      label: 'Tendência (3 observações)',
      value: trendLabel,
    },
  ];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 1 }}
      >
        <Stack direction='row' spacing={0.7} useFlexGap flexWrap='wrap' alignItems='center'>
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.6,
                bgcolor: vacancyColor,
              }}
            />
            <Typography variant='body2'>Vacância</Typography>
          </Stack>
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.6,
                bgcolor: delinquencyColor,
              }}
            />
            <Typography variant='body2'>Inadimplência</Typography>
          </Stack>
          <Stack direction='row' spacing={0.6} alignItems='center'>
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.6,
                bgcolor: occupancyColor,
              }}
            />
            <Typography variant='body2'>Ocupação adimplente</Typography>
          </Stack>
        </Stack>
        <Stack direction='row' spacing={0.7} useFlexGap flexWrap='wrap'>
          {(['6M', '12M', '24M', 'TUDO'] as const).map((item) => (
            <Button
              key={item}
              size='small'
              variant={range === item ? 'contained' : 'outlined'}
              onClick={() => setRange(item)}
              sx={{
                minWidth: 52,
                ...(range === item
                  ? {
                      bgcolor: colors.actions.range.selectedBg,
                      borderColor: colors.actions.range.selectedBorder,
                      color: colors.actions.range.selectedText,
                      '&:hover': {
                        bgcolor: colors.actions.range.selectedHoverBg,
                        borderColor: colors.actions.range.selectedHoverBorder,
                      },
                    }
                  : {
                      color: colors.actions.range.unselectedText,
                      borderColor: colors.actions.range.unselectedBorder,
                      '&:hover': {
                        color: colors.actions.range.unselectedHoverText,
                        borderColor: colors.actions.range.unselectedHoverBorder,
                        bgcolor: colors.actions.range.unselectedHoverBg,
                      },
                    }),
              }}
            >
              {item}
            </Button>
          ))}
        </Stack>
      </Stack>

      <Box
        sx={{
          mb: 1.1,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 0.8,
        }}
      >
        {summaryItems.map((item) => (
          <Paper key={item.label} variant='outlined' sx={{ p: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              {item.label}
            </Typography>
            <Typography variant='subtitle2' fontWeight={800}>
              {item.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <svg viewBox={`0 0 ${width} ${height}`} width='100%' role='img'>
        <rect x={left} y={top} width={chartWidth} height={chartHeight} fill='transparent' stroke='hsl(var(--border))' />
        {[0, 0.2, 0.4, 0.6, 0.8, 1].map((tick) => {
          const y = bottom - tick * chartHeight;
          const value = tick * 100;
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='3 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {PERCENT_FORMATTER.format(value)}%
              </text>
            </g>
          );
        })}
        {chartData.map((item, index) => {
          const x = left + index * (barWidth + barGap);
          const vacancy = item.vacancy ?? 0;
          const occupancy = item.occupancy ?? Math.max(0, 100 - vacancy);
          const delinquencyInsideOccupancy = Math.min(occupancy, Math.max(0, item.delinquency ?? 0));
          const occupiedAdimplente = Math.max(0, occupancy - delinquencyInsideOccupancy);
          const vacancyHeight = (vacancy / 100) * chartHeight;
          const occupiedAdimplenteHeight = (occupiedAdimplente / 100) * chartHeight;
          const delinquencyHeight = (delinquencyInsideOccupancy / 100) * chartHeight;
          const vacancyY = bottom - vacancyHeight;
          const occupiedAdimplenteY = vacancyY - occupiedAdimplenteHeight;
          const delinquencyY = occupiedAdimplenteY - delinquencyHeight;
          const labelY = delinquencyY - 6;
          const vacancyLabelY = vacancyHeight >= 16 ? vacancyY + vacancyHeight / 2 + 4 : vacancyY - 6;
          const delinquencyLabelY =
            delinquencyHeight >= 14 ? delinquencyY + delinquencyHeight / 2 + 4 : delinquencyY - 4;
          const showDelinquencyLabel = delinquencyInsideOccupancy >= 0.5 && delinquencyHeight >= 14;
          return (
            <g key={item.date}>
              <rect
                x={x}
                y={occupiedAdimplenteY}
                width={barWidth}
                height={occupiedAdimplenteHeight}
                fill={occupancyColor}
                rx={2}
              />
              {delinquencyHeight > 0 ? (
                <rect
                  x={x}
                  y={delinquencyY}
                  width={barWidth}
                  height={delinquencyHeight}
                  fill={delinquencyColor}
                  rx={2}
                />
              ) : null}
              <rect x={x} y={vacancyY} width={barWidth} height={vacancyHeight} fill={vacancyColor} rx={2} />
              <text x={x + barWidth / 2} y={labelY} textAnchor='middle' fontSize='11' fill='hsl(var(--foreground))'>
                {`${PERCENT_FORMATTER.format(occupancy)}%`}
              </text>
              {vacancy > 0 ? (
                <text
                  x={x + barWidth / 2}
                  y={vacancyLabelY}
                  textAnchor='middle'
                  fontSize='11'
                  fill={vacancyHeight >= 16 ? colors.semantic.white : vacancyColor}
                  fontWeight={700}
                >
                  {`${PERCENT_FORMATTER.format(vacancy)}%`}
                </text>
              ) : null}
              {showDelinquencyLabel ? (
                <text
                  x={x + barWidth / 2}
                  y={delinquencyLabelY}
                  textAnchor='middle'
                  fontSize='10'
                  fill={colors.semantic.white}
                  fontWeight={700}
                >
                  {`${PERCENT_FORMATTER.format(delinquencyInsideOccupancy)}%`}
                </text>
              ) : null}
              {index % Math.ceil(chartData.length / 6) === 0 || index === chartData.length - 1 ? (
                <text
                  x={x + barWidth / 2}
                  y={height - 24}
                  textAnchor='middle'
                  fontSize='12'
                  fill='hsl(var(--muted-foreground))'
                >
                  {monthLabel(item.date)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}
